import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { FaixaSugestaoVinculoEnum } from '@shared/enums/compras/faixa-sugestao-vinculo.enum';
import { OrigemImportacaoXmlNFeEntradaEnum } from '@shared/enums/suprimentos/origem-importacao-xml-nfe-entrada.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { PessoaLookupService } from '@shared/services/apis/pessoa/pessoa-lookup.service';
import { NotaFiscalEntradaGavetaListarRequest } from '@shared/services/requests/orquestrador-compras/nota-fiscal-entrada-gaveta-listar.request';
import { ListPessoaIdsRequest } from '@shared/services/requests/pessoa/list-pessoa-ids-request';
import { NotaFiscalEntradaGavetaStruct } from '@shared/services/structs/orquestrador-compras/nota-fiscal-entrada-gaveta.struct';
import { UtilService } from '@shared/services/util.service';
import {
  ImportarXmlNotaFiscalEntradaDialogComponent,
  ImportarXmlNotaFiscalEntradaDialogResult,
} from '../../../nota-fiscal-entrada-lista/dialogs/importar-xml-nota-fiscal-entrada-dialog/importar-xml-nota-fiscal-entrada-dialog.component';
import { ImportarXmlNotaFiscalEntradaDialogData } from '../../../nota-fiscal-entrada-lista/dialogs/importar-xml-nota-fiscal-entrada-dialog/importar-xml-nota-fiscal-entrada-dialog-data';

/** Termo mínimo para buscar na gaveta inteira; abaixo disso a lista mostra as notas sugeridas. */
const TAMANHO_MINIMO_FILTRO = 2;

/** Nota da gaveta como o card mostra: rótulo pronto e diferença para o valor do pedido. */
class NotaSeletor {
  nota: NotaFiscalEntradaGavetaStruct;
  rotulo: string;
  /** Valor da nota − valor do pedido, arredondado em centavos. `null` quando o valor do pedido é desconhecido. */
  diferenca: number | null = null;
  /** Faixa da sugestão (`FaixaSugestaoVinculoEnum`); `null` = fora das faixas ("Outros"). */
  faixa: number | null = null;
}

/**
 * Seletor de NF do processo de compras, no layout do design (lista única com filtro): sem termo,
 * mostra as notas sugeridas da gaveta (fornecedor ou produtos do pedido), separadas por faixa e,
 * dentro dela, as mais próximas do valor primeiro; com termo, busca na gaveta inteira por número
 * ou chave, separando pelas mesmas faixas e "Outros". "Importar XML" ao lado do filtro. Só escolhe a nota — emite o `idNFeEntrada` e o chamador
 * roda o vínculo. Usado no modal "Vincular nota fiscal" e embutido na etapa Faturado do card.
 */
@Component({
  selector: 'app-seletor-nota-fiscal',
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AbntPipe,
    LoadingBoxComponent,
  ],
  templateUrl: './seletor-nota-fiscal.component.html',
  styleUrl: './seletor-nota-fiscal.component.css',
})
export class SeletorNotaFiscalComponent implements OnChanges, OnDestroy {
  /** Seções da lista, na ordem das faixas; rótulos iguais aos do modal de vínculo. */
  readonly faixas: { faixa: FaixaSugestaoVinculoEnum; rotulo: string }[] = [
    { faixa: FaixaSugestaoVinculoEnum.FornecedorEProduto, rotulo: 'Sugeridos' },
    { faixa: FaixaSugestaoVinculoEnum.SomenteFornecedor, rotulo: 'Mesmo fornecedor, sem produto em comum' },
    { faixa: FaixaSugestaoVinculoEnum.SomenteProduto, rotulo: 'Produto em comum, fornecedor diferente' },
  ];

  @Input({ required: true }) idCompra!: number;
  /** Bloqueia todas as ações; o tooltip mostra `motivoBloqueio`. */
  @Input() bloqueado = false;
  @Input() motivoBloqueio = '';
  /** O chamador está rodando o vínculo com a nota escolhida. */
  @Input() ocupado = false;
  @Output() notaEscolhida = new EventEmitter<number>();
  /** Nome do fornecedor do pedido, assim que carregado (o modal mostra no subtítulo). */
  @Output() fornecedorCarregado = new EventEmitter<string>();

  readonly skeletonSlots = [1, 2, 3];

  /** Nota cujo "Vincular" foi clicado — mostra o spinner no botão enquanto `ocupado`. */
  idNotaEscolhida: number | null = null;
  /** A nota escolhida veio do "Importar XML": o spinner do vínculo fica nesse botão. */
  isXmlImportado = false;

  // Pedido
  valorPedido: number | null = null;
  nomeFornecedor = '';
  /** Fornecedor do pedido: a nota emitida por ele sem nome no XML mostra o nome do cadastro. */
  private idFornecedorPedido: number | null = null;

  // Sugeridas
  sugeridas: NotaSeletor[] = [];
  carregandoSugeridas = false;
  semFornecedor = false;
  totalSugeridas = 0;

  // Busca
  termo = '';
  resultados: NotaSeletor[] = [];
  buscando = false;
  temMais = false;
  avisoChave = '';
  private paginaBusca = 0;
  private tokenBusca = 0;
  private totalBusca = 0;
  private readonly termo$ = new Subject<void>();

  private readonly destroy$ = new Subject<void>();
  private readonly idCompraAlterado$ = new Subject<void>();

  constructor(
    private compraService: CompraService,
    private notasService: NotaFiscalEntradaService,
    private pessoaLookupService: PessoaLookupService,
    private dialog: MatDialog,
    private alertService: AlertService,
  ) {
    this.termo$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => this.buscar());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idCompra']) this.carregarSugeridas();
    if (changes['ocupado'] && !this.ocupado) {
      this.idNotaEscolhida = null;
      this.isXmlImportado = false;
    }
  }

  ngOnDestroy(): void {
    this.idCompraAlterado$.next();
    this.idCompraAlterado$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Ações bloqueadas pela regra (`bloqueado`) ou enquanto o vínculo roda (`ocupado`). */
  get acoesDesabilitadas(): boolean {
    return this.bloqueado || this.ocupado;
  }

  /**
   * Motivo do bloqueio (tooltip). Durante o vínculo não há aviso de espera: o spinner fica no botão acionado.
   * Vazio = sem tooltip.
   */
  get motivoAcoesDesabilitadas(): string {
    return this.bloqueado ? this.motivoBloqueio || 'O vínculo com a NF não pode ser alterado agora.' : '';
  }

  /** Com termo, a lista é o resultado da busca na gaveta; sem termo, as notas sugeridas. */
  get isFiltrando(): boolean {
    return this.termo.trim().length >= TAMANHO_MINIMO_FILTRO;
  }

  get quantidadeDisponivel(): number {
    return this.isFiltrando ? this.totalBusca : this.totalSugeridas;
  }

  /** Seções da lista: as três faixas, na ordem, e "Outros" (fora das faixas). Seção vazia não aparece. */
  secoes(itens: NotaSeletor[]): { rotulo: string; itens: NotaSeletor[] }[] {
    return [
      ...this.faixas.map(f => ({ rotulo: f.rotulo, itens: itens.filter(i => i.faixa === f.faixa) })),
      { rotulo: 'Outros', itens: itens.filter(i => i.faixa == null) },
    ].filter(secao => secao.itens.length > 0);
  }

  alterarTermo(valor: string): void {
    this.termo = valor ?? '';
    this.avisoChave = '';
    if (!this.isFiltrando) {
      // Voltou às notas sugeridas: descarta a busca em andamento e o resultado anterior.
      this.tokenBusca++;
      this.buscando = false;
      this.resultados = [];
      this.totalBusca = 0;
      this.temMais = false;
      return;
    }
    this.termo$.next();
  }

  escolher(nota: NotaFiscalEntradaGavetaStruct): void {
    if (this.acoesDesabilitadas) return;
    this.idNotaEscolhida = nota.idNFeEntrada;
    this.isXmlImportado = false;
    this.notaEscolhida.emit(nota.idNFeEntrada);
  }

  // ---------------------------------------------------------------- sugeridas

  private carregarSugeridas(): void {
    this.idCompraAlterado$.next();
    this.sugeridas = [];
    this.totalSugeridas = 0;
    this.semFornecedor = false;
    this.valorPedido = null;
    this.nomeFornecedor = '';
    if (!this.idCompra) return;

    this.carregandoSugeridas = true;
    this.compraService.getDetalhe(this.idCompra)
      .pipe(takeUntil(this.idCompraAlterado$), takeUntil(this.destroy$))
      .subscribe({
        next: (detalhe) => {
          if (detalhe.isError) {
            this.carregandoSugeridas = false;
            this.alertService.show('Erro', detalhe.errorDescription || 'Não foi possível carregar o pedido.', AlertType.error);
            return;
          }
          this.valorPedido = detalhe.compra?.valorTotal ?? null;
          // Busca já feita antes do detalhe chegar: agora dá para mostrar a diferença de valor.
          this.resultados = this.resultados.map(r => this.paraNotaSeletor(r.nota));
          const idFornecedor = detalhe.compra?.idFornecedor ?? null;
          this.idFornecedorPedido = idFornecedor;
          this.semFornecedor = !idFornecedor;
          // Item avulso (sem produto) não conta para a sugestão.
          const idsProduto = [...new Set((detalhe.itens ?? [])
            .map(item => item.idProduto)
            .filter((id): id is number => id != null))];
          if (idFornecedor) this.carregarNomeFornecedor(idFornecedor);
          if (!idFornecedor && !idsProduto.length) {
            this.carregandoSugeridas = false;
            return;
          }
          this.listarNotasSugeridas(idFornecedor, idsProduto);
        },
        error: () => {
          this.carregandoSugeridas = false;
          this.alertService.show('Erro', 'Não foi possível carregar o pedido.', AlertType.error);
        },
      });
  }

  /**
   * Gaveta em modo sugestão: notas do fornecedor do pedido OU com algum produto dele, cada uma com a
   * sua faixa (as seções da lista).
   */
  private listarNotasSugeridas(idFornecedor: number | null, idsProduto: number[]): void {
    const request = new NotaFiscalEntradaGavetaListarRequest();
    request.pesquisar = null;
    request.listIdPessoaEmitente = null;
    request.dataDe = null;
    request.dataAte = null;
    request.idPessoaEmitenteSugestao = idFornecedor;
    request.listIdProdutoSugestao = idsProduto.length ? idsProduto : null;

    this.notasService.listarGaveta(request, 0)
      .pipe(takeUntil(this.idCompraAlterado$), takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          this.carregandoSugeridas = false;
          if (resposta.isError) {
            this.alertService.show('Erro',
              resposta.errorDescription || 'Não foi possível carregar as notas sugeridas.', AlertType.error);
            return;
          }
          const notas = resposta.list ?? [];
          this.totalSugeridas = resposta.fullListSize ?? notas.length;
          // Menor diferença de valor primeiro (nota sem valor vai para o fim); no empate, a
          // emissão mais recente. As seções filtram por faixa e mantêm esta ordem.
          this.sugeridas = notas
            .map(n => this.paraNotaSeletor(n))
            .sort((a, b) => {
              const porValor = Math.abs(a.diferenca ?? Number.POSITIVE_INFINITY) - Math.abs(b.diferenca ?? Number.POSITIVE_INFINITY);
              return porValor !== 0 && !Number.isNaN(porValor) ? porValor : this.tempoEmissao(b.nota) - this.tempoEmissao(a.nota);
            });
          // Busca já feita antes das sugeridas chegarem: a nota sugerida passa a usar a faixa da sugestão.
          this.resultados = this.resultados.map(r => this.paraNotaSeletor(r.nota));
        },
        error: () => {
          this.carregandoSugeridas = false;
          this.alertService.show('Erro', 'Não foi possível carregar as notas sugeridas.', AlertType.error);
        },
      });
  }

  /** Nome do fornecedor do pedido, pelo mesmo lote da gaveta. Falhou: avisa e fica sem nome. */
  private carregarNomeFornecedor(idFornecedor: number): void {
    const request = new ListPessoaIdsRequest();
    request.listIdPessoa = [idFornecedor];
    this.pessoaLookupService.getListPessoaIds(request)
      .pipe(takeUntil(this.idCompraAlterado$), takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          if (resposta.isError) {
            this.alertService.show('Erro',
              resposta.errorDescription || 'Não foi possível carregar o fornecedor do pedido.', AlertType.error);
            return;
          }
          const pessoa = resposta.listaPessoa?.find(p => p.idPessoa === idFornecedor);
          this.nomeFornecedor = pessoa ? (pessoa.nomeFantasia || pessoa.nome || '') : '';
          this.fornecedorCarregado.emit(this.nomeFornecedor);
        },
        error: () => {
          this.nomeFornecedor = '';
          this.alertService.show('Erro', 'Não foi possível carregar o fornecedor do pedido.', AlertType.error);
        },
      });
  }

  // ---------------------------------------------------------------- busca

  buscar(mais = false): void {
    if (this.acoesDesabilitadas || !this.isFiltrando || (mais && this.buscando)) return;
    const termo = this.termo.trim();
    // Só dígitos e comprido demais para número de nota: provavelmente uma chave incompleta.
    this.avisoChave = /^\d+$/.test(termo) && termo.length > 20 && termo.length !== 44
      ? `A chave de acesso tem 44 dígitos; o termo informado tem ${termo.length}.`
      : '';

    const pagina = mais ? this.paginaBusca + 1 : 0;
    const request = new NotaFiscalEntradaGavetaListarRequest();
    request.pesquisar = termo || null;
    request.listIdPessoaEmitente = null;
    request.dataDe = null;
    request.dataAte = null;

    const token = ++this.tokenBusca;
    this.buscando = true;
    if (!mais) this.resultados = [];
    this.notasService.listarGaveta(request, pagina)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resposta) => {
          if (token !== this.tokenBusca) return;
          this.buscando = false;
          if (resposta.isError) {
            this.temMais = false;
            this.totalBusca = 0;
            this.alertService.show('Erro', resposta.errorDescription || 'Não foi possível buscar as notas.', AlertType.error);
            return;
          }
          this.totalBusca = resposta.fullListSize ?? 0;
          const recebidas = (resposta.list ?? []).map(n => this.paraNotaSeletor(n));
          // A página é calculada pelo servidor: um vínculo feito entre as páginas desloca o conjunto
          // e pode repetir nota já carregada (o @for quebra com track duplicado).
          const ids = new Set(this.resultados.map(r => r.nota.idNFeEntrada));
          this.resultados = mais ? [...this.resultados, ...recebidas.filter(r => !ids.has(r.nota.idNFeEntrada))] : recebidas;
          this.paginaBusca = pagina;
          this.temMais = recebidas.length > 0 && this.resultados.length < (resposta.fullListSize ?? 0);
        },
        error: () => {
          if (token !== this.tokenBusca) return;
          this.buscando = false;
          this.temMais = false;
          this.alertService.show('Erro', 'Não foi possível buscar as notas.', AlertType.error);
        },
      });
  }

  // ---------------------------------------------------------------- XML

  enviarXml(): void {
    if (this.acoesDesabilitadas) return;
    const data = new ImportarXmlNotaFiscalEntradaDialogData();
    data.origem = OrigemImportacaoXmlNFeEntradaEnum.KanbanCompras;
    this.dialog.open<ImportarXmlNotaFiscalEntradaDialogComponent, ImportarXmlNotaFiscalEntradaDialogData, ImportarXmlNotaFiscalEntradaDialogResult>(
      ImportarXmlNotaFiscalEntradaDialogComponent,
      {
        width: '960px',
        maxWidth: '96vw',
        maxHeight: '92vh',
        minHeight: '640px',
        panelClass: 'importar-xml-nota-fiscal-entrada-dialog',
        disableClose: true,
        autoFocus: false,
        data,
      },
    ).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(resultado => {
        if (!resultado?.idNFeEntrada) return;
        // A nota importada entra na gaveta: se o vínculo for cancelado, ela já aparece na lista.
        this.recarregarLista();
        this.idNotaEscolhida = null;
        this.isXmlImportado = true;
        this.notaEscolhida.emit(resultado.idNFeEntrada);
      });
  }

  private recarregarLista(): void {
    this.carregarSugeridas();
    if (this.isFiltrando) this.buscar();
  }

  // ---------------------------------------------------------------- apoio

  private isDoFornecedor(nota: NotaFiscalEntradaGavetaStruct): boolean {
    return this.idFornecedorPedido != null && nota.idPessoaEmitente === this.idFornecedorPedido;
  }

  private paraNotaSeletor(nota: NotaFiscalEntradaGavetaStruct): NotaSeletor {
    const item = new NotaSeletor();
    item.nota = nota;
    item.rotulo = this.rotuloNota(nota);
    item.diferenca = this.valorPedido !== null && nota.valorTotal !== null
      ? UtilService.arredondarAbnt((nota.valorTotal ?? 0) - this.valorPedido, 2)
      : null;
    item.faixa = this.faixaNota(nota);
    return item;
  }

  /**
   * Faixa vinda da gaveta em modo sugestão. Na busca (sem faixa), vale a da nota sugerida já
   * carregada; sem ela, "mesmo fornecedor" quando o emitente é o fornecedor do pedido — a busca
   * não traz os produtos da nota.
   */
  private faixaNota(nota: NotaFiscalEntradaGavetaStruct): number | null {
    if (nota.faixaSugestao != null) return nota.faixaSugestao;
    const sugerida = this.sugeridas.find(s => s.nota.idNFeEntrada === nota.idNFeEntrada);
    if (sugerida) return sugerida.faixa;
    return this.isDoFornecedor(nota) ? FaixaSugestaoVinculoEnum.SomenteFornecedor : null;
  }

  /** Nome do XML; sem ele, o do cadastro quando a nota é do fornecedor do pedido. */
  emitente(nota: NotaFiscalEntradaGavetaStruct): string {
    if (nota.nomeEmitente) return nota.nomeEmitente;
    if (this.nomeFornecedor && nota.idPessoaEmitente != null && nota.idPessoaEmitente === this.idFornecedorPedido) {
      return this.nomeFornecedor;
    }
    return 'Emitente não identificado';
  }

  /** Número/série como no agrupamento do quadro: sem zeros à esquerda; série vazia vira 1. */
  private rotuloNota(nota: NotaFiscalEntradaGavetaStruct): string {
    if (!nota.numero) return 'NF —';
    return `NF ${nota.numero.replace(/^0+(?=\d)/, '')}/${nota.serie || '1'}`;
  }

  private tempoEmissao(nota: NotaFiscalEntradaGavetaStruct): number {
    return nota.dataEmissao ? new Date(nota.dataEmissao).getTime() : 0;
  }

  valorAbsoluto(valor: number): number {
    return Math.abs(valor);
  }
}
